/* eslint-disable @angular-eslint/template/label-has-associated-control */

import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { WidgetApiService } from 'libs/rx-form-builder/src/lib/rx-form-presenter/services/widget-api.service';

export interface SurgeryData {
  diagnoseDate: string;
  surgery: string;
  widgetApiService?: WidgetApiService;
}

@Component({
  selector: 'app-add-surgery-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgSelectModule,
  ],
  template: `
    <h3>Add Surgery</h3>
    <div class="dialog-form">
      <div class="form-field">
        <label>Surgery Date</label>
        <input
          (focus)="picker.open()"
          [matDatepicker]="picker"
          [(ngModel)]="data.diagnoseDate"
          placeholder="MM/DD/YYYY"
        />
        <mat-datepicker #picker></mat-datepicker>
      </div>
      <div class="form-field">
        <label>Select Surgery</label>
        <ng-select
          [(ngModel)]="data.surgery"
          [items]="surgeries"
          bindLabel="label"
          bindValue="value"
          placeholder="Select Surgery"
          [searchable]="true"
          [clearable]="true"
          [addTag]="addTagFn"
          addTagText="Add item"
          [loading]="loading"
          appendTo="body"
          class="ng-select-custom"
        >
          <ng-template ng-option-tmp let-item="item">
            <div class="option-item">
              <span class="option-code" *ngIf="item.code">{{ item.code }}</span>
              <span class="option-label">{{ item.label }}</span>
            </div>
          </ng-template>
        </ng-select>
      </div>
    </div>
    <div class="dialog-actions">
      <button (click)="onCancel()">Cancel</button>
      <button class="blue" (click)="onAdd()" [disabled]="!isValid()">Add</button>
    </div>
  `,
  styles: [
    `
      :host {
        h3 {
          padding: 20px 0 10px;
          margin: 0;
          font-size: 16.5px;
          font-weight: 400;
          color: #000;
        }

        .dialog-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
          max-width: 100%;
          padding: 15px 0;
        }

        .dialog-actions {
          display: flex;
          flex-direction: row;
          justify-content: flex-end;
          gap: 10px;
          padding: 15px 0;

          button {
            font-size: 13.5px;
            font-weight: 400;
            background: transparent;
            color: #000;
            border: solid 1px #ccc;
            padding: 9px 15px;
            border-radius: 5px;
            outline: none;
            cursor: pointer;
            min-width: 75px;

            ::ng-deep {
              .mat-icon {
                width: 19px;
                height: 19px;
              }
            }

            > * {
              &:not(:last-child) {
                margin-right: 10px;
              }
            }

            &.blue {
              background: #1962f3;
              border-color: #1962f3;
              color: white;
              ::ng-deep {
                svg path {
                  fill: white;
                }
              }
            }
          }
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .form-field label {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.6);
          font-weight: 400;
        }

        .form-field input {
          border-radius: 5px;
          border: solid 1px #ccc;
          padding: 8px;
        }

        ::ng-deep .ng-dropdown-panel {
          max-width: 600px !important;
        }
        .option-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }
        .option-code {
          font-weight: 600;
          color: #666;
          min-width: 100px;
          font-size: 12px;
          padding-top: 2px;
        }
        .option-label {
          flex: 1;
          line-height: 1.4;
        }
      }
      ::ng-deep {
        .ng-dropdown-panel.ng-select-custom {
          min-height: 56px;
          // transform: translate(1px, -21px);
          padding: 0;
          margin: 0;
          box-shadow: none;
          border-radius: 0 !important;
        }
      }
    `,
  ],
})
export class AddSurgeryDialogComponent implements OnInit {
  surgeries = [];
  loading = true;

  constructor(
    public dialogRef: MatDialogRef<AddSurgeryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SurgeryData,
  ) {
    if (!data.diagnoseDate) {
      data.diagnoseDate = '';
    }
    if (!data.surgery) {
      data.surgery = '';
    }
  }

  ngOnInit() {
    if (this.data.widgetApiService) {
      this.data.widgetApiService.getSurgeries().subscribe({
        next: (surgeries) => {
          this.surgeries = surgeries.map((surgery: any) => ({
            value: surgery.SurgeryId || '',
            label: surgery.SurgeryName || '',
          }));
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading surgeries:', error);
          this.surgeries = [];
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
    }
  }

  isValid(): boolean {
    return typeof this.data.surgery === 'string'
      ? this.data.surgery.trim().length > 0
      : !!this.data.surgery;
  }

  addTagFn = (term: string) => {
    return term;
  };

  onCancel(): void {
    this.dialogRef.close();
  }

  onAdd(): void {
    if (this.isValid()) {
      const selectedItem = this.surgeries.find((item) => item.value === this.data.surgery);
      if (selectedItem) {
        this.data.surgery = selectedItem.label;
      }
      this.dialogRef.close(this.data);
    }
  }
}
